import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Copy, Archive, Star, Eye, Pencil, FileText, RotateCcw } from "lucide-react";

const APPLIES = { new: "New proposals", renewal: "Renewals", both: "New & renewals" };

export default function TemplatesManager() {
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => api.get(`/templates?include_archived=${showArchived}`).then((r) => setTemplates(r.data));
  useEffect(() => { load(); }, [showArchived]);

  const create = async () => {
    const { data } = await api.post("/templates", { name: "New template" });
    toast.success("Template created");
    nav(`/templates/${data.id}`);
  };
  const duplicate = async (id) => { const { data } = await api.post(`/templates/${id}/duplicate`); toast.success("Duplicated"); load(); nav(`/templates/${data.id}`); };
  const setDefault = async (id) => { await api.post(`/templates/${id}/set-default`); toast.success("Set as default"); load(); };
  const archive = async (t) => { await api.put(`/templates/${t.id}`, { archived: !t.archived }); toast.success(t.archived ? "Restored" : "Archived"); load(); };
  const preview = async (id) => {
    try { const res = await api.get(`/templates/${id}/preview-pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); }
    catch { toast.error("Could not generate preview"); }
  };

  return (
    <div data-testid="templates-manager">
      <div className="flex items-center justify-between mb-6">
        <label className="flex items-center gap-2 text-sm text-cass-slate">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived" />
          Show archived
        </label>
        <Button onClick={create} className="bg-cass-teal text-white" data-testid="create-template-btn"><Plus className="w-4 h-4 mr-1" />New template</Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((t) => (
          <Card key={t.id} className={`p-5 rounded-md flex flex-col ${t.archived ? "opacity-60" : ""}`} data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-cass-navy text-white grid place-items-center"><FileText className="w-5 h-5" /></div>
              <div className="flex gap-1">
                {t.is_default && <Badge className="bg-cass-teal text-white">Default</Badge>}
                {t.archived && <Badge variant="secondary">Archived</Badge>}
              </div>
            </div>
            <h3 className="font-alt font-semibold text-cass-navy mt-3">{t.name}</h3>
            <p className="text-sm text-cass-slate mt-1 flex-1">{t.description || "No description"}</p>
            <div className="flex flex-wrap gap-1 my-3" data-testid={`thumbs-${t.id}`}>
              {(t.pages || []).slice(0, 12).map((pg, i) => (
                <div key={i} title={`${pg.name}${pg.active === false ? " (hidden)" : ""}`} className={`w-5 h-7 rounded-sm border ${pg.active === false ? "opacity-30" : ""} ${pg.type === "cover" ? "bg-cass-navy border-cass-navy" : "bg-white border-border"}`} />
              ))}
              {(t.pages || []).length > 12 && <span className="text-xs text-cass-slate self-center">+{t.pages.length - 12}</span>}
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs text-cass-slate">
              <Badge variant="outline">{APPLIES[t.applies_to] || t.applies_to}</Badge>
              <span>{(t.pages || []).length} pages</span>
            </div>
            <div className="flex flex-wrap gap-1 mt-4 pt-3 border-t border-border">
              <Button size="sm" variant="outline" onClick={() => nav(`/templates/${t.id}`)} data-testid={`edit-template-${t.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
              <Button size="sm" variant="outline" onClick={() => preview(t.id)} data-testid={`preview-template-${t.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Preview</Button>
              <Button size="sm" variant="outline" onClick={() => duplicate(t.id)} data-testid={`dup-template-${t.id}`}><Copy className="w-3.5 h-3.5" /></Button>
              {!t.is_default && <Button size="sm" variant="outline" onClick={() => setDefault(t.id)} title="Set as default" data-testid={`default-template-${t.id}`}><Star className="w-3.5 h-3.5" /></Button>}
              <Button size="sm" variant="outline" onClick={() => archive(t)} data-testid={`archive-template-${t.id}`}>{t.archived ? <RotateCcw className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}</Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
